// askSmart-ui/src/pages/TOSP/portal/tenantSales.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { daysPastDue, YearMonth } from "./portalSummary";
import { amendSales, listSales, SalesApiError, submitSales } from "./tenantSalesApi";

// Tenant-reported sales.
//
// Backed by the server. Percentage rent is billed off these figures, so the two
// rules that matter — a tenant may report a month once, and only the property
// manager may correct it afterwards — are enforced there rather than here: a
// rule the browser keeps is a rule anyone with devtools can break.
//
// Everything that reads sales goes through this file, so the shape of the
// records and the way they're fetched stay in one place.

/** One month of gross sales, as reported by the tenant. */
export interface TenantSalesMonth {
  id: string;
  tenantId: string;
  /** The calendar year the month falls in. */
  year: number;
  /** 1–12. */
  month: number;
  /** Gross sales for the month, as reported. */
  grossSales: number;
  /** ISO timestamp of the submission. */
  submittedAt: string;
  /** Who reported it — the tenant, a delegate acting for them, or an admin. */
  submittedBy: string;
  /** Where from, as the proxy saw it. Stamped server-side, never supplied. */
  submittedIp?: string;
  /** Set when a property manager has since corrected the figure. */
  amendedAt?: string;
  amendedBy?: string;
  amendedIp?: string;
  /** What the tenant originally reported, kept when a figure is corrected. */
  originalGrossSales?: number;
}

export interface SalesYearToDate {
  year: number;
  /** Summed gross sales for the year so far. */
  total: number;
  /** How many months have been reported. */
  monthsReported: number;
  /** The latest month reported (1–12), or null when none have been. */
  lastMonthReported: number | null;
}

// The year's reported sales so far. Returns null when the tenant has reported
// nothing for the year, which is what lets the dashboard distinguish "no sales
// yet" from "$0 of sales" — the second would wrongly read as a trading figure.
export function salesYearToDate(
  sales: TenantSalesMonth[],
  tenantId: string,
  year: number,
): SalesYearToDate | null {
  const forYear = sales.filter(
    (entry) => entry.tenantId === tenantId && entry.year === year,
  );
  if (forYear.length === 0) return null;

  return {
    year,
    total: forYear.reduce((sum, entry) => sum + (entry.grossSales || 0), 0),
    monthsReported: forYear.length,
    lastMonthReported: forYear.reduce((latest, entry) => Math.max(latest, entry.month), 0),
  };
}

export interface YearOnYear {
  current: number;
  previous: number;
  /** Percentage change, or null when last year traded nothing to compare to. */
  changePercent: number | null;
  /** The months both years are compared over. */
  monthsCompared: number;
}

// This year against last year, like for like.
//
// Compared over the months reported THIS year only. Setting a part-year against
// a full previous year would show every tenant running at a loss until
// December, which is a comparison that misleads rather than informs — six
// months of trading is measured against the same six months a year earlier.
export function yearOnYear(
  sales: TenantSalesMonth[],
  tenantId: string,
  year: number,
): YearOnYear | null {
  const mine = sales.filter((entry) => entry.tenantId === tenantId);
  const thisYear = mine.filter((entry) => entry.year === year);
  if (thisYear.length === 0) return null;

  const months = thisYear.map((entry) => entry.month);
  const lastYear = mine.filter(
    (entry) => entry.year === year - 1 && months.includes(entry.month),
  );
  if (lastYear.length === 0) return null;

  const current = thisYear.reduce((sum, entry) => sum + entry.grossSales, 0);
  const previous = lastYear.reduce((sum, entry) => sum + entry.grossSales, 0);

  return {
    current,
    previous,
    // No baseline to be a percentage of — the figures are still shown, just
    // not as a change.
    changePercent: previous === 0 ? null : ((current - previous) / previous) * 100,
    monthsCompared: lastYear.length,
  };
}

// Whether a month has already been reported.
//
// A submitted month is final on the tenant's side: the portal tells them so
// before they confirm, and the write path has to hold them to it or the
// promise is empty. Corrections are an admin action — the property manager can
// amend a figure, or reopen the month for the tenant to re-report — so the
// rule is "the tenant may submit a month once", not "this figure is immutable".
//
// When the container lands, the same rule has to be enforced on the server: a
// POST for a month that already has a record is rejected, and amending goes
// through an admin-only route that records who changed it and when. A check
// that only exists in the browser is a suggestion, not a rule.
export function hasSubmitted(
  sales: TenantSalesMonth[],
  tenantId: string,
  year: number,
  month: number,
): boolean {
  return sales.some(
    (entry) => entry.tenantId === tenantId && entry.year === year && entry.month === month,
  );
}

export interface OwedMonth {
  year: number;
  month: number;
  dueOn: Date;
  /** 0 on the due date itself, higher once it's gone by. */
  daysLate: number;
}

// Months that closed, reached their due date, and haven't been reported —
// oldest first. Includes the month due today, which is owed rather than late;
// callers tell the two apart by `daysLate`.
//
// The span looked at runs from the tenant's earliest reported month to the
// last month that has closed — so a gap in the middle of their history counts,
// but the portal doesn't demand figures from before they were ever reporting.
// A tenant who has reported nothing at all is chased for the last twelve
// months only, rather than back to the start of the lease.
/** What a caller knows about the tenant, beyond the months they have filed. */
export interface OwingContext {
  /**
   * The day they began trading, if it is known.
   *
   * Nothing is owed for a month that ended before they opened. Without it a
   * tenant who has reported nothing is chased for a full year, which for one
   * opening next month is a year of months that never existed.
   */
  openedOn?: string | null;
  /**
   * The day the tenancy ended, if it has.
   *
   * Nothing is owed for a month that began after they left. Without it a past
   * tenancy owes every month from the day it closed until today, for ever —
   * a wall of overdue flags on the traffic lights and a chase in the portal of
   * a business that no longer exists. The month they left IN is still owed:
   * they traded part of it.
   */
  closedOn?: string | null;
  /** False for a tenant whose leases say they file nothing. Absent means yes. */
  reportsSales?: boolean;
}

export function monthsOwing(
  sales: TenantSalesMonth[],
  tenantId: string,
  now: Date,
  dueDay: number,
  /** What the tenant record says about reporting, where the caller has it. */
  tenant: OwingContext = {},
): OwedMonth[] {
  // A tenant who does not report sales cannot be behind on them.
  if (tenant.reportsSales === false) return [];

  const mine = sales.filter((entry) => entry.tenantId === tenantId);

  // The month just gone is the last one that can be owed; this one isn't over.
  let lastClosed = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  // Nor after they left. Parsed off the ISO text for the same timezone reason
  // as the opening date below. The closing MONTH is still owed — a shop that
  // handed over on the 14th traded the first half of it — so the bound is that
  // month, not the one before it.
  if (tenant.closedOn) {
    const [year, month] = tenant.closedOn.split("-").map(Number);
    if (year && month) {
      const closed = new Date(year, month - 1, 1);
      if (closed < lastClosed) lastClosed = closed;
    }
  }
  const earliest = mine.length
    ? mine.reduce(
        (first, entry) =>
          entry.year * 12 + entry.month < first.year * 12 + first.month ? entry : first,
        mine[0],
      )
    : null;

  let from = earliest
    ? new Date(earliest.year, earliest.month - 1, 1)
    : new Date(now.getFullYear(), now.getMonth() - 12, 1);

  // Never before they opened. Parsed off the ISO text rather than through Date,
  // which reads a bare "2026-10-01" as UTC midnight and can land in the month
  // before once a western timezone is applied. The opening MONTH itself is
  // owed — a shop that opened on the 15th still traded that month.
  if (tenant.openedOn) {
    const [year, month] = tenant.openedOn.split("-").map(Number);
    if (year && month) {
      const opened = new Date(year, month - 1, 1);
      if (opened > from) from = opened;
    }
  }

  const owing: OwedMonth[] = [];
  const cursor = new Date(from);

  while (cursor <= lastClosed) {
    const year = cursor.getFullYear();
    const month = cursor.getMonth() + 1;
    const dueOn = new Date(year, month, dueDay);
    const daysLate = daysPastDue(dueOn, now);

    if (daysLate >= 0 && !hasSubmitted(mine, tenantId, year, month)) {
      owing.push({ year, month, dueOn, daysLate });
    }
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return owing;
}

// The best year on record for each month.
//
// Worked out across every year held, not just whichever are on screen — a
// record is a record whether or not the year beside it happens to be showing.
// Needs two years to compare and a figure above nil: one lone month isn't a
// record, and a month where nothing ever traded isn't a best.
export function bestYearForMonth(
  sales: TenantSalesMonth[],
  tenantId: string,
): Map<number, number> {
  const best = new Map<number, number>();

  for (let month = 1; month <= 12; month += 1) {
    const reported = sales.filter(
      (entry) => entry.tenantId === tenantId && entry.month === month,
    );
    if (reported.length < 2) continue;
    const top = reported.reduce((highest, entry) =>
      entry.grossSales > highest.grossSales ? entry : highest,
    );
    if (top.grossSales > 0) best.set(month, top.year);
  }

  return best;
}

export interface MonthMovement {
  up: boolean;
  /** Null when last year's figure was nil — no baseline to be a share of. */
  changePercent: number | null;
}

/** One month against the same month a year earlier. Null when there's no
 *  comparison to make, or the two are identical. */
export function monthOnMonth(
  sales: TenantSalesMonth[],
  tenantId: string,
  year: number,
  month: number,
): MonthMovement | null {
  const find = (forYear: number) =>
    sales.find(
      (entry) =>
        entry.tenantId === tenantId && entry.year === forYear && entry.month === month,
    );

  const current = find(year);
  const prior = find(year - 1);
  if (!current || !prior) return null;

  const change = current.grossSales - prior.grossSales;
  if (change === 0) return null;

  return {
    up: change > 0,
    changePercent:
      prior.grossSales === 0 ? null : Math.abs((change / prior.grossSales) * 100),
  };
}

/** Every year that has a figure against it, newest first. */
export function reportedYears(sales: TenantSalesMonth[], tenantId: string): number[] {
  const years = new Set(
    sales.filter((entry) => entry.tenantId === tenantId).map((entry) => entry.year),
  );
  return [...years].sort((a, b) => b - a);
}

/**
 * ONE TENANT'S reported history — MJ Byrne's — supplied while the import route
 * is built.
 *
 * NOT applied automatically. It used to be: the portal seeded it into any
 * tenant with no figures for these years, which was harmless while there was
 * one tenant and wrong the moment there were two — the second tenant opened
 * their portal and found the first's trading history filed as their own
 * submissions.
 *
 * `seedHistory` is still on the store, but nothing calls it. It writes real
 * figures to a real tenant's record, so it must only ever be run deliberately,
 * against the tenant these numbers actually belong to.
 *
 * Seeded per year, and only into a year the tenant has nothing for, so it can
 * never overwrite a figure reported since. Delete this block once history is
 * imported properly.
 *
 * 2020 is absent rather than empty — no figures were given for it, which is
 * different from a year of no trading. 2021's zeroed January and May, by
 * contrast, are real: months that were reported as nil.
 *
 * 2026 runs to July, the last month closed.
 */
const SEED_SALES: Record<number, number[]> = {
  2019: [
    342737.0, 331444.0, 283491.0, 142467.0, 191203.0, 230336.0, 235998.0, 256498.0,
    246267.0, 217289.0, 198773.0, 276211.96,
  ],
  2021: [
    0.0, 147050.0, 316898.0, 9466.0, 0.0, 71232.0, 279967.0, 285591.0, 234492.23,
    260338.82, 218875.0, 293379.0,
  ],
  2022: [
    369131.0, 431584.58, 425641.5, 231052.06, 229460.67, 288096.38, 390593.22, 393373.22,
    366873.99, 380995.94, 303929.51, 458042.06,
  ],
  2023: [
    544334.9, 579185.77, 490699.71, 230062.75, 274988.17, 329948.0, 395863.22, 399677.2,
    373450.02, 353984.35, 271055.67, 519381.17,
  ],
  2024: [
    534050.87, 598518.87, 446926.31, 240407.46, 300509.8, 381002.0, 388040.79, 422193.38,
    352884.28, 374546.68, 309317.53, 466999.37,
  ],
  2025: [
    499846.71, 555883.74, 472252.4, 219188.65, 327755.25, 340069.01, 301478.98, 390225.45,
    361866.76, 308481.96, 262426.02, 550982.08,
  ],
  2026: [
    633573.53, 617831.29, 426569.17, 251973.01, 273496.99, 342853.44, 363940.1,
  ],
};

export interface StoredSales {
  sales: TenantSalesMonth[];
  /** False once the first load has finished, either way. */
  loading: boolean;
  /** Why the last read or write failed, if it did. */
  error: string | null;
  /** Records a month. The server refuses one already reported. */
  submit: (
    tenantId: string,
    target: YearMonth,
    grossRevenue: number,
    by: string,
  ) => Promise<void>;
  /**
   * Corrects a reported month — the property manager's privilege, and the only
   * way a submitted figure changes. The tenant's original is kept alongside it.
   */
  amend: (
    tenantId: string,
    target: YearMonth,
    grossRevenue: number,
    by: string,
  ) => Promise<void>;
  /** Lays down the seed years for a tenant that has none of them. */
  seedHistory: (tenantId: string) => void;
}

// The one-shot push of browser-held figures to the server was removed on
// 2026-08-27, having done its job: 2019, 2020 and 2021 are on the server in
// full, twelve months each, so nothing is left that exists only in a browser.
//
// It was worth retiring rather than leaving dormant. It sent whatever the
// browser happened to hold, and a browser has held the wrong tenant's figures
// before now. A tenant's own session fails safe — the server rejects a tenantId
// they do not hold — but an admin has access to every tenant, so stale local
// rows would have gone through under their credentials.

// The tenant's sales, kept in one place so the dashboard, the Sales screen and
// the admin's Overview can't hold diverging copies.
//
// The server is the record. What the browser holds is a cache of the last read,
// refreshed after every write, so a figure that failed to save is never left
// looking saved.
export function useStoredSales(): StoredSales {
  const [sales, setSales] = useState<TenantSalesMonth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const rows = await listSales();
    setSales(rows);
    return rows;
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await refresh();
        if (cancelled) return;
        setError(null);
      } catch (err) {
        if (!cancelled) {
          console.error("[TOSP] couldn't load sales:", err);
          setError((err as Error)?.message || "Couldn't load sales.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const submit = useCallback(
    async (tenantId: string, target: YearMonth, grossRevenue: number, _by: string) => {
      // `_by` is ignored: who reported a figure is taken from the token, not
      // from the caller, or the record couldn't be relied on.
      try {
        await submitSales(tenantId, target.year, target.month, grossRevenue);
        await refresh();
        setError(null);
      } catch (err) {
        console.error("[TOSP] couldn't record sales:", err);
        setError((err as Error)?.message || "Couldn't record those sales.");
        // Re-read anyway: a 409 means somebody already reported that month, and
        // the figure that stands is worth showing.
        await refresh().catch(() => undefined);
      }
    },
    [refresh],
  );

  const amend = useCallback(
    async (tenantId: string, target: YearMonth, grossRevenue: number, _by: string) => {
      const entry = sales.find(
        (row) =>
          row.tenantId === tenantId &&
          row.year === target.year &&
          row.month === target.month,
      );
      // Nothing reported means nothing to correct — an admin can't key a figure
      // in on a tenant's behalf through this path, which is the rule the
      // Sales panel already enforces by only offering edit on reported months.
      if (!entry) return;

      try {
        await amendSales(entry.id, grossRevenue);
        await refresh();
        setError(null);
      } catch (err) {
        console.error("[TOSP] couldn't correct sales:", err);
        setError((err as Error)?.message || "Couldn't correct those sales.");
      }
    },
    [refresh, sales],
  );

  // Tenants already seeded this session.
  //
  // Seeding used to run itself in a loop: the effect that calls it depends on
  // this callback, the callback depends on `sales`, and it finishes by calling
  // refresh() — which sets `sales` to a freshly-built array. A new array is a
  // new identity even when the figures are identical, so the callback was
  // rebuilt, the effect refired, and it seeded again. While the writes were
  // succeeding the year guard stopped it after one pass; the moment the server
  // refused them the guard never became true and it ran forever, thousands of
  // rejected POSTs deep, until the rate limiter cut it off.
  //
  // The claim is staked here, before any awaiting, so a second call can't slip
  // in behind the first — and it is NOT released on failure, because a
  // submission the server refuses will be refused again.
  const seededRef = useRef<Set<string>>(new Set());

  const seedHistory = useCallback(
    (tenantId: string) => {
      if (seededRef.current.has(tenantId)) return;
      seededRef.current.add(tenantId);

      void (async () => {
        const pending: { year: number; month: number; grossSales: number }[] = [];

        for (const [key, months] of Object.entries(SEED_SALES)) {
          const year = Number(key);
          // Year by year, and only into one the tenant has nothing for — a year
          // holding even a single reported month is theirs, not the seed's.
          if (sales.some((entry) => entry.tenantId === tenantId && entry.year === year)) {
            continue;
          }
          months.forEach((grossSales, index) =>
            pending.push({ year, month: index + 1, grossSales }),
          );
        }
        if (pending.length === 0) return;

        for (const month of pending) {
          try {
            await submitSales(tenantId, month.year, month.month, month.grossSales);
          } catch (err) {
            // Already there — the seed is meant to be re-runnable.
            if (err instanceof SalesApiError && err.status === 409) continue;
            // Anything else is about this caller or this tenant, not this
            // month: a 403 on January is a 403 on all twelve. Stop rather than
            // sending two dozen more requests that will be refused too.
            console.error("[TOSP] couldn't seed sales history, stopping:", err);
            return;
          }
        }
        await refresh().catch(() => undefined);
      })();
    },
    [refresh, sales],
  );

  return { sales, loading, error, submit, amend, seedHistory };
}
